// /src/data/books/FundametosBlockchain/data/cap13.js
const CAP13 = {
  id: "fundamentos-blockchain-13",
  slug: "evaluacion-de-seguridad-nft",
  title: "Evaluación de Seguridad de los NFTs",
  sections: [
    {
      id: "hero",
      heading: "Evaluación de Seguridad en los Sistemas NFT",
      text: `Los sistemas NFT integran múltiples capas tecnológicas: blockchain, almacenamiento descentralizado, y aplicaciones web.
Cada una puede ser un punto de ataque si no se diseña y protege adecuadamente. 
La seguridad de un ecosistema NFT debe analizarse considerando la autenticidad, integridad, confidencialidad, disponibilidad 
y control de acceso de todos los componentes que intervienen.`
    },

    /* =========================
       1) SPOOFING (SUPLANTACIÓN)
       ========================= */
    {
      id: "spoofing",
      heading: "Spoofing (Suplantación de Identidad)",
      text: `La suplantación ocurre cuando un atacante logra hacerse pasar por otra entidad, usuario o proceso dentro del sistema. 
En el contexto NFT, esto puede significar que alguien adquiera control de una dirección o cuenta sin autorización,
o que modifique la interfaz para engañar al usuario. 
El robo de claves privadas, vulnerabilidades en la autenticación o el uso de sitios falsos son las causas más comunes.`,
      list: [
        { title: "Vectores de ataque", text: "Phishing en interfaces de venta, malware que roba claves, sitios de mint falsos, y explotación de firmas ciegas." },
        { title: "Impacto", text: "Transferencias ilegales de NFTs, robo de fondos, pérdida de confianza y suplantación del emisor original." },
        { title: "Mitigaciones", text: "Verificación formal de contratos, uso de monederos fríos, autenticación multifactor en dApps y extensiones antiphishing en navegadores." },
        { title: "Buenas prácticas", text: "Revisar direcciones, usar dominios ENS verificados y evitar firmar mensajes desconocidos en interfaces no auditadas." }
      ]
    },

    /* =========================
       2) MANIPULACIÓN (INTEGRIDAD)
       ========================= */
    {
      id: "manipulacion",
      heading: "Manipulación (Alteración de Datos)",
      text: `La manipulación consiste en la modificación no autorizada de datos que compromete la integridad del sistema. 
En la blockchain, los registros confirmados son inmutables, pero los elementos almacenados fuera de ella —como imágenes, metadatos o enlaces— 
pueden ser alterados si no se gestionan adecuadamente.`,
      list: [
        { title: "Puntos débiles", text: "Metadatos alojados en servidores centralizados, archivos modificables, o URLs temporales." },
        { title: "Ejemplo", text: "Un atacante reemplaza la imagen del NFT en un servidor HTTP, alterando la representación visual sin cambiar el token on-chain." },
        { title: "Mitigaciones", text: "Usar hashes SHA-256 para los archivos, almacenar contenido en IPFS/Arweave, firmar metadatos y registrar los hashes en la blockchain." },
        { title: "Recomendación", text: "Enviar al comprador tanto el hash como los datos originales del NFT para verificar autenticidad y coincidencia." }
      ]
    },

    /* =========================
       3) REPUDIO (NO REPUDIABILIDAD)
       ========================= */
    {
      id: "repudio",
      heading: "Repudio (No Repudiabilidad)",
      text: `El repudio se da cuando un usuario o entidad niega haber realizado una acción que en realidad sí efectuó.
En los NFTs, esto puede manifestarse cuando un vendedor niega haber transferido un token o cuando un comprador 
pretende rechazar una compra válida.`,
      list: [
        { title: "Garantía en blockchain", text: "Cada transacción está firmada criptográficamente; una vez confirmada, su autoría es verificable públicamente." },
        { title: "Riesgos residuales", text: "Robo de claves, uso de wallets compartidos o controlados por terceros pueden debilitar esta propiedad." },
        { title: "Mitigaciones", text: "Contratos multifirma (multi-sig) o esquemas de aprobación múltiple para autorizar transferencias críticas." },
        { title: "Recomendación", text: "Auditar logs y mantener backups de las claves; emplear soluciones de custodia con trazabilidad y responsabilidad compartida." }
      ]
    },

    /* =========================
       4) FUGA DE INFORMACIÓN (CONFIDENCIALIDAD)
       ========================= */
    {
      id: "fuga-informacion",
      heading: "Fuga de Información (Confidencialidad)",
      text: `En un entorno público como la blockchain, la confidencialidad es limitada. 
Cada transacción y su estado son visibles para cualquier observador. 
Esto puede exponer patrones de uso, direcciones, precios y vínculos entre activos y usuarios.`,
      list: [
        { title: "Vulnerabilidades", text: "Metadatos públicos, trazabilidad de direcciones, y análisis de tráfico on-chain." },
        { title: "Riesgos", text: "Pérdida de privacidad del propietario, exposición de información sensible o asociación de identidad real con direcciones." },
        { title: "Mitigaciones", text: "Usar contratos que preserven privacidad (zk-SNARKs, zkEVM, Tornado Cash-like), ocultar hashes mediante salting o cifrar metadatos." },
        { title: "Buenas prácticas", text: "Evitar almacenar datos personales en metadatos; separar identidades y usar wallets distintos para transacciones sensibles." }
      ]
    },

    /* =========================
       5) NEGACIÓN DE SERVICIO (DISPONIBILIDAD)
       ========================= */
    {
      id: "negacion-de-servicio",
      heading: "Negación de Servicio (DoS)",
      text: `Los ataques de denegación de servicio buscan interrumpir o degradar la disponibilidad de un sistema, 
impidiendo que usuarios legítimos accedan o interactúen con él. 
En el caso de los NFT, pueden dirigirse tanto a la capa blockchain como a los servidores que alojan interfaces o metadatos.`,
      list: [
        { title: "Ataques comunes", text: "Saturación de nodos, spam de transacciones, sobrecarga de gas o bloqueo de servicios externos (APIs, IPFS gateways)." },
        { title: "Impacto", text: "Retrasos en la acuñación, imposibilidad de transferencias o caída de marketplaces centralizados." },
        { title: "Mitigaciones", text: "Distribuir infraestructura, usar CDNs descentralizadas, limitar peticiones por IP, aplicar tarifas dinámicas de gas y fallback nodes." },
        { title: "Nuevas arquitecturas", text: "Modelos híbridos con consenso asíncrono o cadenas secundarias con redundancia activa para mejorar disponibilidad." }
      ]
    },

    /* =========================
       6) ELEVACIÓN DE PRIVILEGIOS (AUTORIZACIÓN)
       ========================= */
    {
      id: "elevacion-de-privilegios",
      heading: "Elevación de Privilegios",
      text: `Ocurre cuando un actor malicioso obtiene permisos o capacidades más allá de los que le corresponden.
En sistemas NFT, esto puede permitirle modificar contratos, acuñar tokens sin autorización o transferir activos ajenos.`,
      list: [
        { title: "Causas", text: "Errores en la lógica de autorización, contratos con funciones sin restricciones (public), y vulnerabilidades en los roles de acceso." },
        { title: "Ejemplo", text: "Un atacante explota un contrato mal diseñado para ejecutar minting ilimitado o modificar precios sin control." },
        { title: "Mitigaciones", text: "Uso estricto de modifiers como onlyOwner o AccessControl; pruebas unitarias de roles y auditorías de permisos." },
        { title: "Buenas prácticas", text: "Desplegar contratos inmutables cuando sea posible, o limitar funciones críticas mediante multisig y timelocks." }
      ]
    },

    /* =========================
       7) CONCLUSIÓN
       ========================= */
    {
      id: "conclusion",
      heading: "Conclusión",
      text: `La seguridad en los sistemas NFT depende de proteger la autenticación, integridad, confidencialidad y disponibilidad 
en todas las capas del ecosistema. 
Spoofing, manipulación, repudio, fuga de información, denegación de servicio y elevación de privilegios 
son amenazas recurrentes que deben abordarse con auditorías, criptografía fuerte, segregación de roles y buenas prácticas de desarrollo seguro. 
Una arquitectura NFT resiliente combina verificaciones on-chain, almacenamiento descentralizado, contratos auditados y controles de acceso 
que preservan la confianza y la trazabilidad de los activos digitales.`
    }
  ]
};

export default CAP13;
